// Conservative convex proxies (docs/specs/2026-09-16-tycoon-composition.md, Part B "Model"). Local geometry is
// (longitudinal, transverse) tiles about the sprite anchor with heights relative to the placement height; the
// transform to absolute tile coordinates is the atlas footprint convention (atlasContract.ts): east = l·cos h − t·sin h,
// south = l·sin h + t·cos h. Left/right are negative/positive transverse.
//
// Legacy proxies are used while an atlas has no component sprites for a structure: they approximate each whole
// legacy sprite from the producer's models (tools/tycoon-sprites/models.py; metres / 20 per tile, metres / 4.082
// per height unit) so production keeps rendering with correct pairwise order where proxies cover the pixels.
import type { FragmentRole, Proxy, XY } from './precedence.js';
import type { ComponentRole } from './atlasComponents.js';
import type { Alignment } from './contracts.js';

export interface LocalProxy { role: FragmentRole; footprint: readonly XY[]; z: readonly [number, number] }
const box = (long: number, t0: number, t1: number): [number, number][] => [[-long, t0], [long, t0], [long, t1], [-long, t1]];

/** Half lengths: track modules are 10 m (0.25 tile half length), station modules 20 m. */
const TRACK_HALF = 0.25, STATION_HALF = 0.5;
export const CAR_HEIGHT_UNITS = 3.5;
export const LEGACY_PROXIES = {
  track: { role: 'rails', footprint: box(TRACK_HALF, -0.2, 0.2), z: [0, 0] },
  'track/deck': { role: 'rails', footprint: box(TRACK_HALF, -0.2, 0.2), z: [0, 0] },
  // Deck plate and girders only; the bent columns below are outside the proxy and take its order.
  trestle: { role: 'solid', footprint: box(TRACK_HALF, -0.225, 0.225), z: [-0.5, 0.37] },
  column: { role: 'solid', footprint: box(0.02, -0.02, 0.02), z: [0, 3] },
  shadow: { role: 'shadow', footprint: box(TRACK_HALF, -0.225, 0.225), z: [0, 0] },
  berm: { role: 'solid', footprint: box(TRACK_HALF, -0.3, 0.3), z: [0, 1] },
  'trench/floor': { role: 'floor', footprint: box(TRACK_HALF, -0.215, 0.215), z: [0, 0] },
  // A legacy wall sprite is placed per lateral side; its proxy is the right-hand wall mirrored by `side`.
  'trench/wall': { role: 'wall-face', footprint: box(TRACK_HALF, 0.205, 0.245), z: [-1.25, 0.06] },
  // One clipped half of a legacy station sprite: platform plus canopy on one side of the track.
  'station/half': { role: 'platform', footprint: box(STATION_HALF, 0.225, 0.75), z: [-0.25, 0.95] },
} as const satisfies Record<string, LocalProxy>;

/**
 * Default component geometry, as the producer authors it (synthetic atlases use it): one-sided components on the
 * right (positive transverse) side, heights relative to the sprite anchor. Ramp faces are flat rectangles anchored to
 * the rail; the runtime clip supplies their visible extent.
 */
export const DEFAULT_COMPONENTS: Record<string, { role: ComponentRole; footprint: [number, number][]; z: [number, number]; side: 'left' | 'right' | null }> = {
  'trench/wall/below-rail': { role: 'bed-wall', footprint: box(TRACK_HALF, 0.205, 0.245), z: [-1.25, 0], side: 'right' },
  'trench/wall/face': { role: 'wall-face', footprint: box(TRACK_HALF, 0.205, 0.245), z: [0, 0.06], side: 'right' },
  // Platforms own their posts, furniture and outer station wall (≤ 3.4 m); each side's canopy owns its roof slab.
  platform: { role: 'platform', footprint: box(STATION_HALF, 0.225, 0.75), z: [-0.25, 0.83], side: 'right' },
  canopy: { role: 'canopy', footprint: box(STATION_HALF, 0.3625, 0.6125), z: [0.7, 0.95], side: 'right' },
  'ramp/track': { role: 'rails', footprint: box(TRACK_HALF, -0.2, 0.2), z: [-1 / 24, 1 / 24], side: null },
  'ramp/floor': { role: 'floor', footprint: box(TRACK_HALF, -0.215, 0.215), z: [-0.2 - 1 / 24, -0.2 + 1 / 24], side: null },
  'ramp/deck': { role: 'deck', footprint: box(TRACK_HALF, -0.225, 0.225), z: [-0.5, 0.37], side: null },
  'ramp/wall/face': { role: 'wall-face', footprint: box(TRACK_HALF, 0.205, 0.245), z: [0, 2.2], side: 'right' },
  'ramp/wall/coping': { role: 'coping', footprint: box(TRACK_HALF, 0.2, 0.26), z: [0, 0.06], side: 'right' },
  'ramp/support/face': { role: 'wall-face', footprint: box(TRACK_HALF, 0.18, 0.2), z: [-3.2, -0.5], side: 'right' },
};
export const defaultComponent = (id: string) => DEFAULT_COMPONENTS[id] ?? DEFAULT_COMPONENTS[id.endsWith('/platform') ? 'platform' : 'canopy']!;

const mirror = (footprint: readonly XY[]): XY[] => footprint.map(([l, t]) => [l, -t] as XY).reverse();
/** Mirrors a right-hand local proxy to the left (negative transverse) side, keeping counter-clockwise order. */
export const sided = (local: LocalProxy, side: -1 | 1): LocalProxy => side === 1 ? local : { ...local, footprint: mirror(local.footprint) };

/** Absolute tile-space proxy for a local proxy at `anchor` (tiles), `heading` (tile-space radians) and base height. */
export function placeProxy(id: string, local: Pick<LocalProxy, 'footprint' | 'z'>, anchor: XY, heading: number, height: number): Proxy {
  const c = Math.cos(heading), s = Math.sin(heading);
  return {
    id,
    footprint: local.footprint.map(([l, t]) => [anchor[0] + l * c - t * s, anchor[1] + l * s + t * c] as XY),
    z: [height + local.z[0], height + local.z[1]],
  };
}

/**
 * Car proxy: pitch-long, body-wide prism from the rail plane up CAR_HEIGHT_UNITS. A covered (tunnel) car stops at
 * ground level, top = min(z0 + 3.5, 0), so it never reaches above ground; open cut and grade are unchanged.
 */
export const carProxy = (id: string, anchor: XY, heading: number, railHeight: number, pitchMeters: number, widthMeters: number, alignment?: Alignment): Proxy => {
  const top = alignment === 'tunnel' ? Math.max(railHeight, Math.min(railHeight + CAR_HEIGHT_UNITS, 0)) : railHeight + CAR_HEIGHT_UNITS;
  return placeProxy(id, { footprint: box(pitchMeters / 40, -widthMeters / 40, widthMeters / 40), z: [0, top - railHeight] }, anchor, heading, railHeight);
};

/** Tile-aligned prism for a city drawable centred in its tile. */
export const tileProxy = (id: string, tile: XY, height: number): Proxy => ({
  id, footprint: [[tile[0] - 0.5, tile[1] - 0.5], [tile[0] + 0.5, tile[1] - 0.5], [tile[0] + 0.5, tile[1] + 0.5], [tile[0] - 0.5, tile[1] + 0.5]], z: [0, height],
});
